import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Send,
  User,
  Shield,
  Calendar,
  AlertCircle,
  MessageSquare,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { SearchSelect, Button } from '../../components/shared';
import {
  getAdminSupportTicket,
  updateAdminSupportTicket,
  replyToSupportTicket,
} from '../../services/supportTicketService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const getStatusColor = (status) => {
  const config = {
    open: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
    in_progress: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    resolved: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  };
  return config[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
};

const getPriorityColor = (priority) => {
  const config = {
    low: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    medium: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    high: 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
    urgent: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  };
  return config[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
};

const AdminSupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const { data: ticketResponse, isLoading, error } = useQuery({
    queryKey: ['adminSupportTicket', id],
    queryFn: () => getAdminSupportTicket(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateAdminSupportTicket(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSupportTicket', id]);
      queryClient.invalidateQueries(['adminSupportTickets']);
      success('Ticket updated successfully');
    },
    onError: (err) => {
      showError(err?.response?.data?.message || 'Failed to update ticket');
    },
  });

  const replyMutation = useMutation({
    mutationFn: (data) => replyToSupportTicket(id, data),
    onSuccess: () => {
      setReplyText('');
      setIsInternal(false);
      queryClient.invalidateQueries(['adminSupportTicket', id]);
      queryClient.invalidateQueries(['adminSupportTickets']);
      success('Reply sent successfully');
    },
    onError: (err) => {
      showError(err?.response?.data?.message || 'Failed to send reply');
    },
  });

  const ticket = ticketResponse?.data;

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    replyMutation.mutate({ response: replyText.trim(), is_internal: isInternal });
  };

  const handleStatusChange = (value) => {
    updateMutation.mutate({ id, data: { status: value } });
  };

  const handlePriorityChange = (value) => {
    updateMutation.mutate({ id, data: { priority: value } });
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading ticket…</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Ticket Not Found</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-4">
            {error?.response?.data?.message || 'This support ticket could not be found.'}
          </p>
          <Button className="!rounded-xl whitespace-nowrap" onClick={() => navigate('/admin/support-tickets')}>
            <ArrowLeft className="w-4 h-4 mr-2 shrink-0" />
            Back to Support Tickets
          </Button>
        </div>
      </div>
    );
  }

  const replies = ticket.ticket_replies || ticket.ticketReplies || [];
  const sortedReplies = [...replies].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={`Ticket #${String(ticket.id).slice(0, 8)}`}
        subtitle={ticket.subject}
        actions={
          <Button
            variant="outline"
            className="!rounded-xl whitespace-nowrap shrink-0"
            onClick={() => navigate('/admin/support-tickets')}
          >
            <ArrowLeft className="w-4 h-4 mr-2 shrink-0" />
            Back to tickets
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.supportTicketDetail.title}
        steps={ADMIN_PAGE_GUIDES.supportTicketDetail.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
            {ticket.status?.replace('_', ' ')}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
            {ticket.priority}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">{ticket.category}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requester</label>
            <div className="flex items-center gap-2 mt-1">
              <User className="w-4 h-4 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-50">{ticket.user?.name || 'N/A'}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{ticket.user?.email}</p>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Assigned To</label>
            <div className="flex items-center gap-2 mt-1">
              <Shield className="w-4 h-4 text-gray-400" />
              <p className="text-gray-900 dark:text-gray-50">{ticket.assignedTo?.name || 'Unassigned'}</p>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</label>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 dark:text-gray-100">{formatDate(ticket.created_at, 'medium')} {formatTime(ticket.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 p-3 rounded-xl bg-gray-50 dark:bg-gray-950/40 border border-gray-200 dark:border-gray-700">
          <div className="w-40">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Status</label>
            <SearchSelect
              value={ticket.status}
              onChange={handleStatusChange}
              options={STATUS_OPTIONS}
              size="sm"
            />
          </div>
          <div className="w-40">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Priority</label>
            <SearchSelect
              value={ticket.priority}
              onChange={handlePriorityChange}
              options={PRIORITY_OPTIONS}
              size="sm"
            />
          </div>
          {!ticket.assigned_to && user?.id && (
            <div className="flex items-end">
              <Button
                className="!rounded-xl"
                onClick={() => updateMutation.mutate({ id, data: { assigned_to: user.id } })}
                disabled={updateMutation.isPending}
              >
                Assign to me
              </Button>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
          <p className="mt-2 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
        </div>

        {ticket.resolution_notes && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Resolution Notes</label>
            <p className="mt-2 text-gray-700 dark:text-gray-300">{ticket.resolution_notes}</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Conversation ({sortedReplies.length} {sortedReplies.length === 1 ? 'reply' : 'replies'})
        </h3>

        <div className="space-y-4 mb-6">
          {sortedReplies.map((reply) => (
            <div
              key={reply.id}
              className={`p-4 rounded-xl ${
                reply.response_type === 'admin'
                  ? 'bg-sky-50 dark:bg-sky-950/30 border-l-4 border-sky-500'
                  : 'bg-gray-50 dark:bg-gray-950/40 border-l-4 border-gray-300 dark:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {reply.response_type === 'admin' ? (
                    <Shield className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                  ) : (
                    <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900 dark:text-gray-50">
                    {reply.responder?.name || 'Support'}
                  </span>
                  {reply.is_internal && (
                    <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 rounded">
                      Internal
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(reply.created_at, 'medium')} {formatTime(reply.created_at)}
                </span>
              </div>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {reply.response_text || reply.response}
              </p>
            </div>
          ))}
        </div>

        <form onSubmit={handleReply} className="space-y-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={replyMutation.isPending}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="rounded border-gray-300"
              />
              Internal note (not visible to user)
            </label>
            <Button
              type="submit"
              className="!rounded-xl inline-flex items-center gap-2"
              disabled={!replyText.trim() || replyMutation.isPending}
            >
              <Send className="w-4 h-4" />
              {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSupportTicketDetail;
